import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import {
  Handshake,
  Plus,
  Copy,
  Check,
  ExternalLink,
  Trash2,
  Loader2,
  Users,
  MessageSquare,
  Star,
} from 'lucide-react';
import type { DealRoomStatus, DealRoomSummaryResponse } from '@clareefai/types';
import { useGetDealRoomsQuery, useDeleteDealRoomMutation } from '../../store/vendorApi';

const STATUS_STYLES: Record<DealRoomStatus, { bg: string; text: string; label: string }> = {
  ACTIVE: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', label: 'Active' },
  DRAFT: { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-600', label: 'Draft' },
  DISABLED: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Disabled' },
};

const DealRooms: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: rooms, isLoading } = useGetDealRoomsQuery();
  const [deleteDealRoom] = useDeleteDealRoomMutation();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const copyLink = async (room: DealRoomSummaryResponse) => {
    await navigator.clipboard.writeText(room.publicUrl);
    setCopiedId(room.id);
    toast.success(t('dealRooms.linkCopied', 'Share link copied'));
    setTimeout(() => setCopiedId((id) => (id === room.id ? null : id)), 2000);
  };

  const remove = async (room: DealRoomSummaryResponse) => {
    if (!window.confirm(t('dealRooms.confirmDelete', 'Delete this deal room? The share link will stop working.'))) return;
    setDeletingId(room.id);
    try {
      await deleteDealRoom(room.id).unwrap();
      toast.success(t('dealRooms.deleted', 'Deal room deleted'));
    } catch {
      toast.error(t('dealRooms.deleteError', 'Could not delete. Please try again.'));
    } finally {
      setDeletingId(null);
    }
  };

  const statusBadge = (room: DealRoomSummaryResponse) => {
    if (room.isExpired) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-50 border-amber-200 text-amber-700">
          {t('dealRooms.expired', 'Expired')}
        </span>
      );
    }
    const s = STATUS_STYLES[room.status];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${s.bg} ${s.text}`}>
        {t(`dealRooms.status.${room.status}`, s.label)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Handshake className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('dealRooms.title', 'Deal Rooms')}</h1>
            <p className="text-sm text-gray-600">
              {t('dealRooms.subtitle', 'Curate reviews into a shareable proof page for a specific prospect.')}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/deal-rooms/new')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4" />
          {t('dealRooms.create', 'Create deal room')}
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
        </div>
      ) : !rooms || rooms.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
          <Handshake className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-900">{t('dealRooms.emptyTitle', 'No deal rooms yet')}</p>
          <p className="text-sm text-gray-600 mt-1 mb-4">
            {t('dealRooms.emptyHint', 'Build a tailored page of reviews and send it to a prospect to help close the deal.')}
          </p>
          <button
            onClick={() => navigate('/deal-rooms/new')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4" />
            {t('dealRooms.create', 'Create deal room')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {rooms.map((room) => (
              <div key={room.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
                <button
                  onClick={() => navigate(`/deal-rooms/${room.id}`)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 truncate">{room.name}</span>
                    {statusBadge(room)}
                  </div>
                  <p className="text-sm text-gray-500 truncate">
                    {room.prospectName || room.prospectCompany
                      ? [room.prospectName, room.prospectCompany].filter(Boolean).join(' · ')
                      : t('dealRooms.noProspect', 'No prospect set')}
                  </p>
                </button>

                <div className="hidden md:flex items-center gap-6 text-sm text-gray-600 flex-shrink-0">
                  <span className="inline-flex items-center gap-1" title={t('dealRooms.reviews', 'Reviews') as string}>
                    <Star className="w-4 h-4 text-gray-400" />
                    {room.reviewCount}
                  </span>
                  <span className="inline-flex items-center gap-1" title={t('dealRooms.visitors', 'Unique visitors') as string}>
                    <Users className="w-4 h-4 text-gray-400" />
                    {room.uniqueVisitors.toLocaleString()}
                  </span>
                  <span className="inline-flex items-center gap-1" title={t('dealRooms.leads', 'Leads') as string}>
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    {room.leads.toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => copyLink(room)}
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                    title={t('dealRooms.copyLink', 'Copy share link') as string}
                  >
                    {copiedId === room.id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <a
                    href={room.publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                    title={t('dealRooms.openPage', 'Open page') as string}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => remove(room)}
                    disabled={deletingId === room.id}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                    title={t('dealRooms.delete', 'Delete') as string}
                  >
                    {deletingId === room.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealRooms;
